import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getBuilderAiCredits,
  rechargeBuilderAiCredits,
  updateBuilderAiCreditsSettings
} from '../../services/api/implementation/impl';
import Button from '../../../shared/components/ui/Button';

export default function EditBuilderAiCredits({ builder, onClose, onSuccess }) {
  const builderId = builder?.builder_id;
  const [tokens, setTokens] = useState('10000');
  const [note, setNote] = useState('');
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [monthlyCap, setMonthlyCap] = useState('0');
  const [rpm, setRpm] = useState('30');

  const { data, isLoading } = useQuery({
    queryKey: ['builderAiCredits', builderId],
    queryFn: () => getBuilderAiCredits(builderId),
    enabled: !!builderId
  });

  useEffect(() => {
    const credits = data?.credits;
    if (!credits) return;
    setLlmEnabled(credits.llmEnabled !== false);
    setMonthlyCap(String(credits.monthlyTokenCap ?? 0));
    setRpm(String(credits.requestsPerMinute ?? 30));
  }, [data]);

  const rechargeMutation = useMutation({
    mutationFn: () =>
      rechargeBuilderAiCredits(builderId, {
        tokens: parseInt(tokens, 10),
        note: note.trim() || undefined
      }),
    onSuccess: () => {
      toast.success('AI tokens recharged');
      onSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Recharge failed');
    }
  });

  const settingsMutation = useMutation({
    mutationFn: () =>
      updateBuilderAiCreditsSettings(builderId, {
        llmEnabled,
        monthlyTokenCap: parseInt(monthlyCap, 10) || 0,
        requestsPerMinute: parseInt(rpm, 10) || 30,
        rechargeNote: note.trim() || null
      }),
    onSuccess: () => {
      toast.success('AI settings saved');
      onSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Save failed');
    }
  });

  const balance = data?.credits?.creditBalance ?? 0;

  return (
    <div className="space-y-4 p-1">
      <div>
        <p className="text-sm font-semibold text-gray-900">{builder?.builder_name}</p>
        <p className="text-xs text-gray-500">Builder ID {builderId}</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading balance…</p>
      ) : (
        <div className="rounded-xl border border-teal-100 bg-teal-50/50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">
            Current balance
          </p>
          <p className="text-2xl font-bold text-[#1E4A53]">
            {Number(balance).toLocaleString('en-IN')} tokens
          </p>
          <p className="mt-1 text-xs text-gray-600">
            Used this month: {Number(data?.credits?.tokensUsedThisMonth ?? 0).toLocaleString('en-IN')}
          </p>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-600">Recharge tokens</label>
        <input
          type="number"
          min="1"
          value={tokens}
          onChange={(e) => setTokens(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="e.g. 50000"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-600">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Invoice #, payment ref, etc."
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-600">Monthly cap (0 = none)</label>
          <input
            type="number"
            min="0"
            value={monthlyCap}
            onChange={(e) => setMonthlyCap(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-600">Requests / min</label>
          <input
            type="number"
            min="1"
            value={rpm}
            onChange={(e) => setRpm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={llmEnabled}
          onChange={(e) => setLlmEnabled(e.target.checked)}
        />
        AI features enabled for this builder
      </label>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          type="button"
          onClick={() => rechargeMutation.mutate()}
          disabled={rechargeMutation.isPending || !tokens}>
          Recharge tokens
        </Button>
        <Button
          displayType="secondary"
          type="button"
          onClick={() => settingsMutation.mutate()}
          disabled={settingsMutation.isPending}>
          Save settings
        </Button>
        <Button displayType="secondary" type="button" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
